function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useProject, useReports } from "@sail/observability";
import { Deferred, measure, noop } from "@sail/utils";
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ErrorBoundary from "../../internal/prefetching/ErrorBoundary";
import PrefetchEnvContext from "../../internal/prefetching/PrefetchEnvContext";
import PrefetchErrorView from "../../internal/prefetching/PrefetchErrorView";
import { PrefetchHooksContext } from "../../internal/prefetching/PrefetchHooksContext";
import PruningPrefetchCacheContext from "../../internal/prefetching/PruningPrefetchCacheContext";
const unusedSignal = new AbortController().signal;
export default /*#__PURE__*/ memo(function PrefetchInjector({ children }) {
    const callbacks = useRef([]);
    const counter = useRef(0);
    const [hooks, setHooks] = useState({});
    const removeHook = useCallback(function removeHook(hookId, error) {
        setHooks((prevHooks)=>{
            const newHooks = _object_spread({}, prevHooks);
            delete newHooks[hookId];
            return newHooks;
        });
        const callbacksIndex = callbacks.current.findIndex(({ ids })=>{
            return ids.has(hookId);
        });
        if (callbacksIndex === -1) {
            return;
        }
        const entry = callbacks.current[callbacksIndex];
        const { ids, deferred, errors } = entry;
        var _ids_get;
        const { index, group } = (_ids_get = ids.get(hookId)) !== null && _ids_get !== void 0 ? _ids_get : {};
        if (typeof index !== 'number' || typeof group !== 'string') {
            return;
        }
        if (error) {
            errors[group][index] = error;
        }
        ids.delete(hookId);
        if (ids.size === 0) {
            const result = {};
            callbacks.current.splice(callbacksIndex, 1);
            for(const group in errors){
                if (errors.hasOwnProperty(group)) {
                    for(let i = 0; i < errors[group].length; i++){
                        var // @ts-expect-error - TS2542: Index signature in type 'PrefetchResult<Readonly<Record<string, readonly PrefetchQueriesFn[]>>>' only permits reading.
                        _result, _group;
                        (_result = result)[_group = group] || (_result[_group] = []);
                        if (errors[group].hasOwnProperty(i) && errors[group][i]) {
                            result[group].push({
                                status: 'rejected',
                                reason: errors[group][i]
                            });
                        } else {
                            result[group].push({
                                status: 'fulfilled',
                                value: undefined
                            }); // Return `undefined` to prevent usage.
                        }
                    }
                }
            }
            deferred.resolve(result);
        }
    }, [
        setHooks
    ]);
    const appendHooks = useCallback(function appendHooks(newHooks, options) {
        var _options_signal;
        const signal = (_options_signal = options === null || options === void 0 ? void 0 : options.signal) !== null && _options_signal !== void 0 ? _options_signal : unusedSignal;
        var _options_wrapper;
        const wrapper = (_options_wrapper = options === null || options === void 0 ? void 0 : options.wrapper) !== null && _options_wrapper !== void 0 ? _options_wrapper : Fragment;
        const tokens = options.tokens;
        if (signal.aborted) {
            return Promise.resolve(Object.keys(newHooks).reduce((output, group)=>Object.assign(output, {
                    [group]: newHooks[group].map(()=>({
                            status: 'rejected',
                            reason: signal.reason
                        }))
                }), {}));
        }
        if (!tokens) {
            throw new Error('Tokens are required to prefetch queries');
        }
        const hooksMap = {};
        const ids = new Map();
        const start = performance.now();
        Object.entries(newHooks).forEach(([group, groupHooks])=>{
            groupHooks.forEach((usePrefetchFn, index)=>{
                const id = `${usePrefetchFn.name}-${counter.current++}`;
                hooksMap[id] = {
                    usePrefetchFn,
                    wrapper,
                    fnName: usePrefetchFn.name,
                    pruningSignal: signal,
                    start,
                    token: tokens[group]
                };
                ids.set(id, {
                    index,
                    group
                });
            });
        });
        setHooks((prevHooks)=>_object_spread({}, prevHooks, hooksMap));
        const deferred = new Deferred();
        const emptyErrors = Object.entries(newHooks).reduce((acc, [group])=>_object_spread_props(_object_spread({}, acc), {
                [group]: new Array(newHooks[group].length)
            }), {});
        callbacks.current.push({
            ids,
            deferred: deferred,
            errors: emptyErrors
        });
        const abort = ()=>{
            signal.removeEventListener('abort', abort);
            ids.forEach((_, id)=>removeHook(id, signal.reason));
        };
        signal.addEventListener('abort', abort);
        deferred.promise.catch(noop).then(()=>{
            signal.removeEventListener('abort', abort);
        });
        return deferred.promise;
    }, [
        setHooks,
        removeHook
    ]);
    const prefetchEnvContext = useMemo(()=>{
        return {
            isPrefetchMode: true
        };
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(PrefetchEnvContext.Provider, {
                value: prefetchEnvContext,
                children: Object.entries(hooks).map(([hookId, hook])=>/*#__PURE__*/ _jsx(HookExecutorWithPlatformErrorBoundary, {
                        hookId: hookId,
                        queryDescriptor: hook,
                        removeHook: removeHook
                    }, hookId))
            }),
            /*#__PURE__*/ _jsx(PrefetchHooksContext.Provider, {
                value: appendHooks,
                children: children
            })
        ]
    });
});
const HookExecutorWithPlatformErrorBoundary = /*#__PURE__*/ memo(function HookExecutorWithPlatformErrorBoundary({ hookId, queryDescriptor, removeHook }) {
    const { pruningSignal, wrapper: Wrapper, fnName } = queryDescriptor;
    const { error } = useReports();
    return(// Error boundary for platform code
    /*#__PURE__*/ _jsx(ErrorBoundary, {
        errorView: PrefetchErrorView,
        onError: (err, extras)=>{
            error(err, {
                extras: _object_spread({
                    prefetchHookName: fnName
                }, extras),
                project: 'sail_core'
            });
            removeHook(hookId, err);
        },
        children: /*#__PURE__*/ _jsx(PruningPrefetchCacheContext.Provider, {
            value: pruningSignal,
            children: /*#__PURE__*/ _jsx(Wrapper, {
                children: /*#__PURE__*/ _jsx(ProductErrorBoundary, {
                    prefetchHookName: fnName,
                    removeHook: (err)=>removeHook(hookId, err),
                    children: /*#__PURE__*/ _jsx(HookExecutor, {
                        hookId: hookId,
                        queryDescriptor: queryDescriptor,
                        removeHook: removeHook
                    })
                })
            })
        })
    }));
});
const ProductErrorBoundary = /*#__PURE__*/ memo(function ProductErrorBoundary({ children, prefetchHookName, removeHook }) {
    const { error } = useReports();
    const project = useProject();
    return /*#__PURE__*/ _jsx(ErrorBoundary, {
        errorView: PrefetchErrorView,
        onError: (err, extras)=>{
            error(err, {
                extras: _object_spread({
                    prefetchHookName
                }, extras),
                project
            });
            removeHook(err);
        },
        children: children
    });
});
const HookExecutor = /*#__PURE__*/ memo(function HookExecutor({ hookId, queryDescriptor, removeHook }) {
    const { usePrefetchFn, start, token } = queryDescriptor;
    const { loading, error } = usePrefetchFn({
        token
    });
    const mounted = useRef(false);
    if (!mounted.current) {
        mounted.current = true;
    }
    useEffect(()=>{
        if (!loading) {
            measure(hookId, {
                start,
                detail: {
                    devtools: {
                        dataType: 'track-entry',
                        track: 'PrefetchInjector',
                        trackGroup: '@sail/data',
                        color: 'primary-light',
                        tooltipText: 'During this time the prefetch hook has been mounted'
                    }
                }
            });
            removeHook(hookId, error);
        }
    }, [
        loading,
        hookId,
        removeHook,
        error,
        start
    ]);
    return null;
});

//# sourceMappingURL=PrefetchInjector.js.map